import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { isRecurringCleaningSelector } from '@wecasa/common/src/ducks/customer/basket';
import { getIsBookingForSelfOrDefaultToTrue, getMode } from '@wecasa/common/src/ducks/customer/basket-selectors';
import { getCurrentCountry } from '@wecasa/common/src/utils/country';
import { formatPrice } from '@wecasa/common/src/utils/formatting';

import { CONFIG_BY_COUNTRY } from '@wecasa/customer/src/constants/country-config';
import { useGetCesuWalletQuery } from '@wecasa/customer/src/queries/cesu-queries';

import { translate } from '@wecasa/i18n';

import Alert from '@wecasa/web-ui/src/components/alert/alert';
import Skeleton from '@wecasa/web-ui/src/components/skeleton/skeleton';

import { route } from '../../../../../../steps/steps';

const t = translate('basket.cleaning');

export const CESU_DEDUCTION_TEST_ID = 'cesu-deduction-alert';

const StyledAlert = styled(Alert)`
  margin: 10px 0;
`;

const StyledSkeleton = styled(Skeleton)`
  margin: 10px 0;
`;

type CesuDeductionAlertProps = {
  totalPrice?: number;
};

export const CesuDeductionAlert = ({ totalPrice }: CesuDeductionAlertProps) => {
  const isRecapStep = useLocation().pathname === route('recap');
  const { isCesuAvailable } = CONFIG_BY_COUNTRY[getCurrentCountry()];
  // CESU only applies when creating a booking for self, same rule as tax credit
  const isForSelf = useSelector(getIsBookingForSelfOrDefaultToTrue);
  const isCreation = useSelector(getMode) === 'creation';
  // CESU only covers spot cleaning; the wallet endpoint can't tell spot from recurring, so gate here
  // (same as useHasCesuWallet, temporary until the backend gates it).
  const isRecurringCleaning = useSelector(isRecurringCleaningSelector);
  const shouldShowCesuDeduction = isRecapStep && isCesuAvailable && isForSelf && isCreation && !isRecurringCleaning;
  const { cesuAmount, isLoadingCesuAmount, cesuAmountError } = useGetCesuWalletQuery({
    enabled: shouldShowCesuDeduction,
  });

  if (!shouldShowCesuDeduction) return null;

  if (isLoadingCesuAmount) return <StyledSkeleton linesWidth={['100%']} />;

  // The wallet balance is the only thing that can fail; the deduction still happens on the backend at payment time.
  if (cesuAmountError)
    return <StyledAlert testID={CESU_DEDUCTION_TEST_ID} type='success' content={t('.cesu_deduction_error')} />;

  // Rendered even when empty so tests can await a settled query before asserting the notice is absent.
  if (!cesuAmount || !totalPrice) return <div data-testid={CESU_DEDUCTION_TEST_ID} />;

  return (
    <StyledAlert
      testID={CESU_DEDUCTION_TEST_ID}
      type='success'
      content={t('.cesu_deduction_notice', {
        deducted_amount: formatPrice(Math.min(totalPrice, cesuAmount)),
        wallet_balance: formatPrice(cesuAmount),
      })}
    />
  );
};
